import type {
  ProjectImportReviewItemView,
  ProjectImportReviewPanelView,
  ProjectImportSourceReference,
} from "@/types/projects";
import type {
  StoryImportBulkAcceptElementType,
  StoryImportConfidence,
  StoryImportConfidenceDetails,
  StoryImportDraft,
  StoryImportErrorCode,
  StoryImportJob,
  StoryImportRelationshipSuggestion,
  StoryImportRequiredResolution,
  StoryImportReviewBundle,
  StoryImportReviewElement,
  StoryImportReviewGroup,
  StoryImportSourceEvidence,
  StoryImportSummary,
  StoryImportValidationResult,
} from "@/features/story-import/story-import.types";
import {
  inferStoryImportFormat,
  isStoryImportReviewElementType,
} from "@/features/story-import/story-import.types";

const storyImportStageKeys = [
  "normalize",
  "detect_language",
  "chunk_narrative",
  "extract_entities",
  "extract_lore",
  "extract_relationships",
  "extract_timeline",
  "build_governance",
  "build_story_spine",
  "consolidate_duplicates",
  "validate_results",
  "review_ready",
] as const;

type StoryImportStageKey = (typeof storyImportStageKeys)[number];

const storyImportLegacyStageMap: Record<string, StoryImportStageKey> = {
  detect_metadata: "detect_language",
  extract_entities: "extract_entities",
  extract_governance: "build_governance",
  extract_lore: "extract_lore",
  extract_story_spine: "build_story_spine",
  extract_timeline: "extract_timeline",
  prepare_review: "validate_results",
  split_chapters: "chunk_narrative",
};

const storyImportStageLabelMap: Record<StoryImportStageKey, string> = {
  build_governance: "Building governance",
  build_story_spine: "Building story spine",
  chunk_narrative: "Chunking narrative",
  consolidate_duplicates: "Consolidating duplicates",
  detect_language: "Detecting language",
  extract_entities: "Extracting entities",
  extract_lore: "Extracting lore",
  extract_relationships: "Extracting relationships",
  extract_timeline: "Extracting timeline",
  normalize: "Normalizing document",
  review_ready: "Review ready",
  validate_results: "Validating results",
};

export function normalizeStoryImportStageKey(
  stageKey: string | null | undefined,
): StoryImportStageKey | null {
  if (!stageKey) {
    return null;
  }

  if ((storyImportStageKeys as readonly string[]).includes(stageKey)) {
    return stageKey as StoryImportStageKey;
  }

  return storyImportLegacyStageMap[stageKey] ?? null;
}

export function getStoryImportStageIndex(stageKey: string | null | undefined) {
  const normalizedStageKey = normalizeStoryImportStageKey(stageKey);

  return normalizedStageKey
    ? storyImportStageKeys.indexOf(normalizedStageKey)
    : -1;
}

export function getStoryImportStageLabel(stageKey: string | null | undefined) {
  const normalizedStageKey = normalizeStoryImportStageKey(stageKey);
  return normalizedStageKey ? storyImportStageLabelMap[normalizedStageKey] : null;
}

export function getStoryImportPassDisplayLabel(
  job: Pick<StoryImportJob, "currentStageKey" | "passLabel"> | null | undefined,
) {
  return job?.passLabel ?? getStoryImportStageLabel(job?.currentStageKey) ?? null;
}

function sanitizeStorageFileName(fileName: string) {
  return fileName
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120);
}

export function createStoryImportDraft(
  file: File,
  projectId: string | null = null,
): StoryImportDraft {
  const detectedFormat = inferStoryImportFormat(file.name, file.type) ?? "txt";

  return {
    detectedFormat,
    file,
    fileName: file.name,
    fileSizeBytes: file.size,
    mimeType: file.type || "application/octet-stream",
    projectId,
    sessionId: globalThis.crypto.randomUUID(),
    storagePath: null,
  };
}

export function buildStoryImportStoragePath(
  userId: string,
  sessionId: string,
  fileName: string,
) {
  return `${userId}/draft/${sessionId}/${sanitizeStorageFileName(fileName)}`;
}

export function mapStoryImportValidationError(
  result: Extract<StoryImportValidationResult, { ok: false }>,
): StoryImportErrorCode {
  return result.code === "IMPORT_FILE_TOO_LARGE" ? "file-too-large" : "invalid-file";
}

export function mapImportJobRecord(row: {
  completed_at?: string | null;
  credits_used?: number | null;
  current_pass?: number | null;
  current_stage_key?: string | null;
  detected_language?: string | null;
  error_text?: string | null;
  file_path?: string | null;
  id: string;
  pass_label?: string | null;
  payload_path?: string | null;
  project_id?: string | null;
  retry_count?: number | null;
  source_file_name?: string | null;
  status?: string | null;
  summary_path?: string | null;
  total_passes?: number | null;
}): StoryImportJob {
  const currentStageKey = normalizeStoryImportStageKey(row.current_stage_key);
  const derivedPassNumber = getStoryImportStageIndex(currentStageKey);

  return {
    completedAt: row.completed_at ?? null,
    creditsUsed: row.credits_used ?? 0,
    currentPass:
      typeof row.current_pass === "number"
        ? row.current_pass
        : Math.max(0, derivedPassNumber),
    currentStageKey,
    detectedLanguage: row.detected_language ?? null,
    errorText: row.error_text ?? null,
    filePath: row.file_path ?? null,
    jobId: row.id,
    passLabel: row.pass_label ?? getStoryImportStageLabel(currentStageKey),
    payloadPath: row.payload_path ?? null,
    projectId: row.project_id ?? null,
    retryCount: row.retry_count ?? 0,
    sourceFileName: row.source_file_name ?? null,
    status:
      row.status === "queued"
      || row.status === "pending"
      || row.status === "processing"
      || row.status === "canceling"
      || row.status === "canceled"
      || row.status === "review_ready"
      || row.status === "importing"
      || row.status === "complete"
      || row.status === "completed"
      || row.status === "failed"
        ? row.status
        : "draft",
    summaryPath: row.summary_path ?? null,
    totalPasses: row.total_passes ?? 12,
  };
}

function mapStoryImportConfidence(value: string | null | undefined): StoryImportConfidence {
  if (value === "low" || value === "medium") {
    return value;
  }

  return "high";
}

function mapStoryImportConfidenceDetails(input: unknown): StoryImportConfidenceDetails | null {
  if (!input || typeof input !== "object") {
    return null;
  }

  const candidate = input as Record<string, unknown>;
  const score = typeof candidate.score === "number" ? candidate.score : null;
  if (score === null) {
    return null;
  }

  return {
    band: mapStoryImportConfidence(
      typeof candidate.band === "string" ? candidate.band : null,
    ),
    citationStrength:
      typeof candidate.citationStrength === "number" ? candidate.citationStrength : 0,
    contradictionPenalty:
      typeof candidate.contradictionPenalty === "number" ? candidate.contradictionPenalty : 0,
    crossChunkSupport:
      typeof candidate.crossChunkSupport === "number" ? candidate.crossChunkSupport : 0,
    modelConfidence:
      typeof candidate.modelConfidence === "number" ? candidate.modelConfidence : 0,
    resolutionScore:
      typeof candidate.resolutionScore === "number" ? candidate.resolutionScore : 0,
    schemaCompleteness:
      typeof candidate.schemaCompleteness === "number" ? candidate.schemaCompleteness : 0,
    score,
  };
}

function mapStoryImportSourceEvidence(input: unknown): StoryImportSourceEvidence | null {
  if (!input || typeof input !== "object") {
    return null;
  }

  const candidate = input as Record<string, unknown>;
  const excerpt = typeof candidate.excerpt === "string" ? candidate.excerpt : "";
  const reason = typeof candidate.reason === "string" ? candidate.reason : "";

  if (!excerpt && !reason) {
    return null;
  }

  return {
    chapterLabel:
      typeof candidate.chapterLabel === "string" ? candidate.chapterLabel : null,
    chapterNumbers: Array.isArray(candidate.chapterNumbers)
      ? candidate.chapterNumbers.filter((value): value is number => typeof value === "number")
      : [],
    contradictionFlag: candidate.contradictionFlag === true,
    excerpt,
    explicitness:
      candidate.explicitness === "inferred"
        ? "inferred"
        : "explicit",
    paragraphLabel:
      typeof candidate.paragraphLabel === "string" ? candidate.paragraphLabel : null,
    reason,
    sourceRef:
      typeof candidate.sourceRef === "string" ? candidate.sourceRef : null,
    temporalMarker:
      typeof candidate.temporalMarker === "string" ? candidate.temporalMarker : null,
  };
}

function mapStoryImportRequiredResolution(input: unknown): StoryImportRequiredResolution {
  if (!input || typeof input !== "object") {
    return {
      isProjectUnlocked: false,
      pendingChapterCount: 0,
      pendingStorySpineCount: 0,
    };
  }

  const candidate = input as Record<string, unknown>;
  const pendingChapterCount =
    typeof candidate.pendingChapterCount === "number" ? candidate.pendingChapterCount : 0;
  const pendingStorySpineCount =
    typeof candidate.pendingStorySpineCount === "number" ? candidate.pendingStorySpineCount : 0;

  return {
    isProjectUnlocked:
      typeof candidate.isProjectUnlocked === "boolean"
        ? candidate.isProjectUnlocked
        : pendingChapterCount === 0 && pendingStorySpineCount === 0,
    pendingChapterCount,
    pendingStorySpineCount,
  };
}

export function mapStoryImportReviewElement(input: unknown): StoryImportReviewElement | null {
  if (!input || typeof input !== "object") {
    return null;
  }

  const candidate = input as Record<string, unknown>;
  const displayTitle = typeof candidate.displayTitle === "string" ? candidate.displayTitle.trim() : "";
  const previewText = typeof candidate.previewText === "string" ? candidate.previewText.trim() : "";
  const elementType =
    typeof candidate.elementType === "string" && isStoryImportReviewElementType(candidate.elementType)
      ? candidate.elementType
      : "chapter";

  return {
    acceptedAt: typeof candidate.acceptedAt === "string" ? candidate.acceptedAt : null,
    acceptedRecordId: typeof candidate.acceptedRecordId === "string" ? candidate.acceptedRecordId : null,
    confidence: mapStoryImportConfidence(
      typeof candidate.confidence === "string" ? candidate.confidence : null,
    ),
    confidenceDetails: mapStoryImportConfidenceDetails(candidate.confidenceDetails),
    confidenceScore:
      typeof candidate.confidenceScore === "number" ? candidate.confidenceScore : null,
    discardedAt: typeof candidate.discardedAt === "string" ? candidate.discardedAt : null,
    displayTitle: displayTitle || "Imported item",
    editorTarget:
      candidate.editorTarget === "entity"
      || candidate.editorTarget === "lore"
      || candidate.editorTarget === "timeline"
      || candidate.editorTarget === "governance"
        ? candidate.editorTarget
        : "story",
    elementData:
      candidate.elementData && typeof candidate.elementData === "object"
        ? candidate.elementData as Record<string, unknown>
        : {},
    elementType,
    evidence: Array.isArray(candidate.evidence)
      ? candidate.evidence
        .map((entry) => mapStoryImportSourceEvidence(entry))
        .filter((entry): entry is StoryImportSourceEvidence => entry !== null)
      : [],
    id: typeof candidate.id === "string" ? candidate.id : crypto.randomUUID(),
    needsReview: candidate.needsReview === true,
    notes: Array.isArray(candidate.notes)
      ? candidate.notes.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      : [],
    previewText: previewText || displayTitle || "Imported manuscript candidate",
    proposalTypeLabel:
      typeof candidate.proposalTypeLabel === "string" ? candidate.proposalTypeLabel : null,
    reviewStatus:
      candidate.reviewStatus === "accepted"
      || candidate.reviewStatus === "discarded"
      || candidate.reviewStatus === "merged"
      || candidate.reviewStatus === "linked"
        ? candidate.reviewStatus
        : "pending",
  };
}

export function mapStoryImportReviewBundle(input: unknown): StoryImportReviewBundle | null {
  if (!input || typeof input !== "object") {
    return null;
  }

  const candidate = input as Record<string, unknown>;

  return {
    chapters: Array.isArray(candidate.chapters)
      ? candidate.chapters
        .map((chapter, index) => {
          if (!chapter || typeof chapter !== "object") {
            return null;
          }

          const value = chapter as Record<string, unknown>;
          const title = typeof value.title === "string" ? value.title : `Imported chapter ${index + 1}`;
          const bodyText = typeof value.bodyText === "string" ? value.bodyText : "";
          if (!bodyText.trim()) {
            return null;
          }

          return {
            bodyText,
            chapterNumber:
              typeof value.chapterNumber === "number" ? value.chapterNumber : index + 1,
            endMarker: typeof value.endMarker === "string" ? value.endMarker : null,
            endOffset: typeof value.endOffset === "number" ? value.endOffset : 0,
            sortOrder: typeof value.sortOrder === "number" ? value.sortOrder : index,
            startMarker: typeof value.startMarker === "string" ? value.startMarker : title,
            startOffset: typeof value.startOffset === "number" ? value.startOffset : 0,
            title,
          };
        })
        .filter((chapter): chapter is StoryImportReviewBundle["chapters"][number] => chapter !== null)
      : [],
    createdAt:
      typeof candidate.createdAt === "string" ? candidate.createdAt : new Date().toISOString(),
    detectedLanguage:
      typeof candidate.detectedLanguage === "string" ? candidate.detectedLanguage : "English",
    documentMetadata:
      candidate.documentMetadata && typeof candidate.documentMetadata === "object"
        ? {
            author:
              typeof (candidate.documentMetadata as Record<string, unknown>).author === "string"
                ? (candidate.documentMetadata as Record<string, unknown>).author as string
                : null,
            estimatedWordCount:
              typeof (candidate.documentMetadata as Record<string, unknown>).estimatedWordCount === "number"
                ? (candidate.documentMetadata as Record<string, unknown>).estimatedWordCount as number
                : 0,
            title:
              typeof (candidate.documentMetadata as Record<string, unknown>).title === "string"
                ? (candidate.documentMetadata as Record<string, unknown>).title as string
                : null,
          }
        : {
            author: null,
            estimatedWordCount: 0,
            title: null,
          },
    elements: Array.isArray(candidate.elements)
      ? candidate.elements
        .map((element) => mapStoryImportReviewElement(element))
        .filter((element): element is StoryImportReviewElement => element !== null)
      : [],
    extractedTextHash:
      typeof candidate.extractedTextHash === "string" ? candidate.extractedTextHash : "",
    governanceDocId:
      typeof candidate.governanceDocId === "string" ? candidate.governanceDocId : "",
    jobId: typeof candidate.jobId === "string" ? candidate.jobId : "",
    projectId: typeof candidate.projectId === "string" ? candidate.projectId : "",
    relationshipSuggestions: Array.isArray(candidate.relationshipSuggestions)
      ? candidate.relationshipSuggestions
        .map((entry) => {
          if (!entry || typeof entry !== "object") {
            return null;
          }
          const value = entry as Record<string, unknown>;
          const fromName = typeof value.fromName === "string" ? value.fromName.trim() : "";
          const toName = typeof value.toName === "string" ? value.toName.trim() : "";
          const label = typeof value.label === "string" ? value.label.trim() : "";
          const reason = typeof value.reason === "string" ? value.reason.trim() : "";
          return fromName && toName && label && reason
            ? { fromName, label, reason, toName }
            : null;
        })
        .filter(
          (entry): entry is StoryImportReviewBundle["relationshipSuggestions"][number] => entry !== null,
        )
      : [],
    requiredResolution: mapStoryImportRequiredResolution(candidate.requiredResolution),
    sourceFileName:
      typeof candidate.sourceFileName === "string" ? candidate.sourceFileName : "Imported manuscript",
    storyId: typeof candidate.storyId === "string" ? candidate.storyId : "",
    storySpineId: typeof candidate.storySpineId === "string" ? candidate.storySpineId : "",
    updatedAt:
      typeof candidate.updatedAt === "string" ? candidate.updatedAt : new Date().toISOString(),
  };
}

export function mapStoryImportSummaryArtifact(input: {
  completedAt?: string | null;
  detectedFormat?: string | null;
  detectedLanguage?: string | null;
  elementCounts?: Array<{
    count?: number | null;
    elementType?: string | null;
  }> | null;
  estimatedWordCount?: number | null;
  jobId?: string | null;
  missingElementTypes?: string[] | null;
  passResults?: Array<{
    confidence?: string | null;
    count?: number | null;
    label?: string | null;
    passNumber?: number | null;
  }> | null;
  projectId?: string | null;
  sourceFileName?: string | null;
}): StoryImportSummary {
  const detectedFormat =
    typeof input.detectedFormat === "string" && input.detectedFormat.length > 0
      ? inferStoryImportFormat(`source.${input.detectedFormat}`) ?? "txt"
      : "txt";

  return {
    completedAt: input.completedAt ?? null,
    detectedFormat,
    detectedLanguage: input.detectedLanguage ?? null,
    elementCounts: (input.elementCounts ?? [])
      .map((count) => {
        const elementType =
          typeof count.elementType === "string" && isStoryImportReviewElementType(count.elementType)
            ? count.elementType
            : null;

        return elementType
          ? {
              count: count.count ?? 0,
              elementType,
            }
          : null;
      })
      .filter((count): count is StoryImportSummary["elementCounts"][number] => count !== null),
    estimatedWordCount: input.estimatedWordCount ?? 0,
    jobId: input.jobId ?? globalThis.crypto.randomUUID(),
    missingElementTypes: (input.missingElementTypes ?? []).filter((value) =>
      isStoryImportReviewElementType(value),
    ),
    passResults: (input.passResults ?? []).map((passResult, index) => ({
      confidence: mapStoryImportConfidence(passResult.confidence),
      count: passResult.count ?? 0,
      label: passResult.label?.trim() || `Pass ${index}`,
      passNumber: passResult.passNumber ?? index,
    })),
    projectId: input.projectId ?? null,
    sourceFileName: input.sourceFileName ?? "Imported manuscript",
  };
}

function mapRelationshipSuggestionToReviewElement(
  suggestion: StoryImportRelationshipSuggestion,
  index: number,
): StoryImportReviewElement {
  const relationshipLabel = `${suggestion.fromName} -> ${suggestion.toName}`;

  return {
    acceptedAt: null,
    acceptedRecordId: null,
    confidence: "low",
    confidenceDetails: null,
    confidenceScore: 0.3,
    discardedAt: null,
    displayTitle: relationshipLabel,
    editorTarget: "entity",
    elementData: {
      fromName: suggestion.fromName,
      label: suggestion.label,
      reason: suggestion.reason,
      toName: suggestion.toName,
    },
    elementType: "relationship",
    evidence: [{
      chapterLabel: null,
      chapterNumbers: [],
      contradictionFlag: false,
      excerpt: relationshipLabel,
      explicitness: "inferred",
      paragraphLabel: null,
      reason: suggestion.reason,
      sourceRef: null,
      temporalMarker: null,
    }],
    id: `relationship-suggestion-${index}-${suggestion.fromName}-${suggestion.toName}`,
    needsReview: true,
    notes: [suggestion.reason],
    previewText: suggestion.label,
    proposalTypeLabel: null,
    reviewStatus: "pending",
  };
}

function mapStoryImportEvidenceToProjectSourceReference(
  evidence: StoryImportSourceEvidence,
): ProjectImportSourceReference {
  return {
    chunkIndex: evidence.chapterNumbers[0] ?? 0,
    endOffset: 0,
    excerpt: evidence.excerpt,
    reason: evidence.reason,
    startOffset: 0,
  };
}

function getProjectImportReviewItemHref(input: {
  acceptedRecordId: string | null;
  elementType: StoryImportReviewElement["elementType"];
  projectId: string;
  reviewRoute: string;
  reviewStatus: StoryImportReviewElement["reviewStatus"];
}) {
  if (input.reviewStatus === "pending") {
    return input.reviewRoute;
  }

  if (input.reviewStatus !== "accepted" || !input.acceptedRecordId) {
    return null;
  }

  switch (input.elementType) {
    case "chapter":
      return `/projects/${input.projectId}/stories?chapterId=${input.acceptedRecordId}`;
    case "character":
    case "item":
      return `/projects/${input.projectId}/entities/cards/${input.acceptedRecordId}`;
    case "location":
    case "world_system":
      return `/projects/${input.projectId}/lore/entries/${input.acceptedRecordId}`;
    case "governance":
    case "story_spine":
      return `/projects/${input.projectId}/lore`;
    case "timeline_event":
      return `/projects/${input.projectId}/timeline`;
    case "relationship":
      return `/projects/${input.projectId}/entities`;
    default:
      return null;
  }
}

function mapStoryImportElementToProjectReviewItem(input: {
  element: StoryImportReviewElement;
  projectId: string;
  reviewRoute: string;
}): ProjectImportReviewItemView {
  return {
    confidence: input.element.confidence,
    displayTitle: input.element.displayTitle,
    elementId: input.element.id,
    elementType: input.element.elementType,
    href: getProjectImportReviewItemHref({
      acceptedRecordId: input.element.acceptedRecordId,
      elementType: input.element.elementType,
      projectId: input.projectId,
      reviewRoute: input.reviewRoute,
      reviewStatus: input.element.reviewStatus,
    }),
    sourceRefs: input.element.evidence.map(mapStoryImportEvidenceToProjectSourceReference),
  };
}

const reviewGroupDefinitions = [
  {
    descriptionKey: "storyImport.review.groups.chapter.description",
    emptyMessageKey: "storyImport.review.groups.chapter.empty",
    elementType: "chapter",
    titleKey: "storyImport.review.groups.chapter.title",
  },
  {
    descriptionKey: "storyImport.review.groups.character.description",
    emptyMessageKey: "storyImport.review.groups.character.empty",
    elementType: "character",
    titleKey: "storyImport.review.groups.character.title",
  },
  {
    descriptionKey: "storyImport.review.groups.item.description",
    emptyMessageKey: "storyImport.review.groups.item.empty",
    elementType: "item",
    titleKey: "storyImport.review.groups.item.title",
  },
  {
    descriptionKey: "storyImport.review.groups.location.description",
    emptyMessageKey: "storyImport.review.groups.location.empty",
    elementType: "location",
    titleKey: "storyImport.review.groups.location.title",
  },
  {
    descriptionKey: "storyImport.review.groups.worldSystem.description",
    emptyMessageKey: "storyImport.review.groups.worldSystem.empty",
    elementType: "world_system",
    titleKey: "storyImport.review.groups.worldSystem.title",
  },
  {
    descriptionKey: "storyImport.review.groups.governance.description",
    emptyMessageKey: "storyImport.review.groups.governance.empty",
    elementType: "governance",
    titleKey: "storyImport.review.groups.governance.title",
  },
  {
    descriptionKey: "storyImport.review.groups.storySpine.description",
    emptyMessageKey: "storyImport.review.groups.storySpine.empty",
    elementType: "story_spine",
    titleKey: "storyImport.review.groups.storySpine.title",
  },
  {
    descriptionKey: "storyImport.review.groups.timeline.description",
    emptyMessageKey: "storyImport.review.groups.timeline.empty",
    elementType: "timeline_event",
    titleKey: "storyImport.review.groups.timeline.title",
  },
  {
    descriptionKey: "storyImport.review.groups.relationship.description",
    emptyMessageKey: "storyImport.review.groups.relationship.empty",
    elementType: "relationship",
    titleKey: "storyImport.review.groups.relationship.title",
  },
] as const;

export function sortStoryImportReviewElements(elements: StoryImportReviewElement[]) {
  const confidenceRank: Record<StoryImportConfidence, number> = {
    low: 0,
    medium: 1,
    high: 2,
  };
  const statusRank = {
    pending: 0,
    accepted: 1,
    linked: 2,
    merged: 3,
    discarded: 4,
  } as const;

  return [...elements].sort((left, right) => {
    if (statusRank[left.reviewStatus] !== statusRank[right.reviewStatus]) {
      return statusRank[left.reviewStatus] - statusRank[right.reviewStatus];
    }

    if (left.needsReview !== right.needsReview) {
      return left.needsReview ? -1 : 1;
    }

    const leftScore = left.confidenceScore ?? (
      left.confidenceDetails?.score
      ?? (left.confidence === "low" ? 0.3 : left.confidence === "medium" ? 0.6 : 0.85)
    );
    const rightScore = right.confidenceScore ?? (
      right.confidenceDetails?.score
      ?? (right.confidence === "low" ? 0.3 : right.confidence === "medium" ? 0.6 : 0.85)
    );
    if (leftScore !== rightScore) {
      return leftScore - rightScore;
    }

    if (confidenceRank[left.confidence] !== confidenceRank[right.confidence]) {
      return confidenceRank[left.confidence] - confidenceRank[right.confidence];
    }

    return left.displayTitle.localeCompare(right.displayTitle, undefined, {
      sensitivity: "base",
    });
  });
}

export function groupStoryImportReviewElements(
  elements: StoryImportReviewElement[],
  relationshipSuggestions: StoryImportRelationshipSuggestion[] = [],
): StoryImportReviewGroup[] {
  const advisoryRelationshipElements = relationshipSuggestions.map((suggestion, index) =>
    mapRelationshipSuggestionToReviewElement(suggestion, index),
  );
  const allElements = [...elements, ...advisoryRelationshipElements];

  return reviewGroupDefinitions.map((definition) => ({
    ...definition,
    bulkAcceptElementType:
      definition.elementType === "relationship"
        ? null
        : definition.elementType as StoryImportBulkAcceptElementType,
    count: allElements.filter((element) => element.elementType === definition.elementType).length,
    items: sortStoryImportReviewElements(
      allElements.filter((element) => element.elementType === definition.elementType),
    ),
    lowConfidenceCount: allElements.filter((element) =>
      element.elementType === definition.elementType && element.confidence === "low"
    ).length,
    needsReviewCount: allElements.filter((element) =>
      element.elementType === definition.elementType && element.needsReview
    ).length,
    pendingCount: allElements.filter((element) =>
      element.elementType === definition.elementType && element.reviewStatus === "pending"
    ).length,
  }));
}

export function mapStoryImportBundleToProjectImportReviewPanel(input: {
  bundle: StoryImportReviewBundle;
  completedAt?: string | null;
  sourceFileName?: string | null;
}): ProjectImportReviewPanelView {
  const reviewRoute = buildProjectStoryImportReviewRoute(
    input.bundle.projectId,
    input.bundle.jobId,
  );
  const advisoryRelationshipElements = input.bundle.relationshipSuggestions.map((suggestion, index) =>
    mapRelationshipSuggestionToReviewElement(suggestion, index),
  );
  const items = sortStoryImportReviewElements([
    ...input.bundle.elements,
    ...advisoryRelationshipElements,
  ]).map((element) =>
    mapStoryImportElementToProjectReviewItem({
      element,
      projectId: input.bundle.projectId,
      reviewRoute,
    }),
  );

  return {
    completedAt: input.completedAt ?? null,
    items,
    jobId: input.bundle.jobId,
    sourceFileName: input.sourceFileName?.trim() || input.bundle.sourceFileName,
  };
}

export function getStoryImportProgressValue(job: Pick<StoryImportJob, "currentPass" | "status" | "totalPasses"> | null | undefined) {
  if (!job) {
    return 0;
  }

  if (
    job.status === "review_ready"
    || job.status === "complete"
    || job.status === "completed"
  ) {
    return job.totalPasses;
  }

  return Math.min(job.totalPasses, Math.max(0, job.currentPass) + 1);
}

export function getStoryImportProgressPercent(job: Pick<StoryImportJob, "currentPass" | "status" | "totalPasses"> | null | undefined) {
  if (!job || job.totalPasses <= 0) {
    return 0;
  }

  return Math.min(100, Math.round((getStoryImportProgressValue(job) / job.totalPasses) * 100));
}

export function shouldRestoreDismissedStoryImportJob(
  previousJob: StoryImportJob | null | undefined,
  nextJob: StoryImportJob,
) {
  const isNewFinalState =
    nextJob.status === "review_ready"
    || nextJob.status === "complete"
    || nextJob.status === "completed"
    || nextJob.status === "failed";
  const isCanceled = nextJob.status === "canceled";

  if (!isNewFinalState && !isCanceled) {
    return false;
  }

  if (!previousJob || previousJob.jobId !== nextJob.jobId) {
    return false;
  }

  return (
    previousJob.status === "queued"
    || previousJob.status === "pending"
    || previousJob.status === "processing"
    || previousJob.status === "canceling"
  );
}

export function getStoryImportTranslationKey(errorCode: StoryImportErrorCode) {
  switch (errorCode) {
    case "access-denied":
      return "storyImport.messages.accessDenied";
    case "file-too-large":
      return "storyImport.messages.fileTooLarge";
    case "file-too-many-pages":
      return "storyImport.messages.fileTooManyPages";
    case "insufficient-credits":
      return "storyImport.messages.insufficientCredits";
    case "invalid-file":
      return "storyImport.messages.invalidFile";
    case "missing-config":
      return "storyImport.messages.missingConfig";
    case "retry-not-allowed":
      return "storyImport.messages.retryNotAllowed";
    case "cancel-not-allowed":
      return "storyImport.messages.cancelNotAllowed";
    case "review-action-failed":
      return "storyImport.messages.reviewActionFailed";
    case "review-element-not-found":
      return "storyImport.messages.reviewElementNotFound";
    case "review-element-resolved":
      return "storyImport.messages.reviewElementResolved";
    case "review-not-ready":
      return "storyImport.messages.reviewNotReady";
    case "source-not-found":
      return "storyImport.messages.sourceNotFound";
    case "start-failed":
      return "storyImport.messages.startFailed";
    case "already-running":
      return "storyImport.messages.alreadyRunning";
    case "tier-locked":
      return "storyImport.messages.tierLocked";
    case "text-extraction-failed":
      return "storyImport.messages.textExtractionFailed";
    case "upload-failed":
      return "storyImport.messages.uploadFailed";
    default:
      return "storyImport.messages.genericError";
  }
}

export function getStoryImportJobErrorTranslationKey(errorText: string | null | undefined) {
  switch (errorText) {
    case "IMPORT_SOURCE_NOT_FOUND":
      return "storyImport.messages.sourceNotFound";
    case "IMPORT_TEXT_EXTRACTION_FAILED":
      return "storyImport.messages.textExtractionFailed";
    case "IMPORT_REVIEW_NOT_READY":
      return "storyImport.messages.reviewNotReady";
    case "IMPORT_RETRY_NOT_ALLOWED":
      return "storyImport.messages.retryNotAllowed";
    case "IMPORT_FILE_TOO_MANY_PAGES":
      return "storyImport.messages.fileTooManyPages";
    case "IMPORT_CANCELED":
      return "storyImport.messages.processingCanceled";
    default:
      return "storyImport.messages.processingFailed";
  }
}

export function getStoryImportStatusKey(status: StoryImportJob["status"]) {
  switch (status) {
    case "queued":
      return "storyImport.processing.status.queued";
    case "pending":
      return "storyImport.processing.status.pending";
    case "processing":
      return "storyImport.processing.status.processing";
    case "canceling":
      return "storyImport.processing.status.canceling";
    case "canceled":
      return "storyImport.processing.status.canceled";
    case "review_ready":
      return "storyImport.processing.status.reviewReady";
    case "importing":
      return "storyImport.processing.status.importing";
    case "complete":
      return "storyImport.processing.status.complete";
    case "completed":
      return "storyImport.processing.status.completed";
    case "failed":
      return "storyImport.processing.status.failed";
    default:
      return "storyImport.processing.status.draft";
  }
}

export function getStoryImportConfidenceKey(confidence: StoryImportConfidence) {
  return `storyImport.confidence.${confidence}` as const;
}

export function getStoryImportElementTypeKey(elementType: string) {
  switch (elementType) {
    case "chapter":
    case "character":
    case "governance":
    case "item":
    case "location":
    case "relationship":
    case "story_spine":
    case "timeline_event":
    case "world_system":
      return `storyImport.elementTypes.${elementType}` as const;
    default:
      return "storyImport.elementTypes.generic";
  }
}

export function getStoryImportReviewStatusKey(status: StoryImportReviewElement["reviewStatus"]) {
  return `storyImport.review.status.${status}` as const;
}

export function createStoryImportSummaryPreview(
  draft: Pick<StoryImportDraft, "detectedFormat" | "fileName">,
): StoryImportSummary {
  return {
    completedAt: null,
    detectedFormat: draft.detectedFormat,
    detectedLanguage: null,
    elementCounts: [],
    estimatedWordCount: 0,
    jobId: globalThis.crypto.randomUUID(),
    missingElementTypes: [],
    passResults: [],
    projectId: null,
    sourceFileName: draft.fileName,
  };
}

export function buildProjectStoryImportRoute(projectId: string) {
  return `/projects/${projectId}/imports/new`;
}

export function buildProjectStoryImportReviewRoute(projectId: string, jobId: string) {
  return `/projects/${projectId}/imports/${jobId}/review`;
}
